import React, { useEffect, useMemo, useState } from 'react';
import { PlayerHeadshot } from '../../../shell/SleeperAvatar';
import RostersPanel from './RostersPanel';
import TweetCard from '../../Tweets/TweetCard';
import { getKTCPickIdFromPick } from '../../../utils/ktcPickIds';

/**
 * TradeRatingView — one option of the rating flow, shell-styled.
 * Rendered inside PackageView's ft-view; all rating state lives in the parent.
 */

const responsiveCss = `
  @media (max-width: 760px) {
    .ftpv-sides { grid-template-columns: 1fr !important; }
    .ftpv-arrow { display: none !important; }
    .ftpv-anchor { display: none !important; }
    .ftpv-verdictword { font-size: 32px !important; }
    .ftpv-circle { width: 38px !important; height: 38px !important; font-size: 13px !important; }
    .ftpv-nav { flex-direction: column-reverse !important; }
    .ftpv-nav .ft-btn { width: 100%; justify-content: center; }
  }
`;

const ordinalRound = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return `${n}`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

const smallCircle = (size) => ({
  width: size, height: size, borderRadius: '50%', background: 'var(--ft-surface-hover)', flex: 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontSize: Math.max(9, size * 0.34), fontWeight: 700, color: 'var(--ft-ink-2)',
});

const TradeRatingView = ({
  trade,
  playerData,
  league,
  tweets = [],
  currentIndex,
  totalTrades,
  rating,
  notes,
  reasons = [],
  onRatingChange,
  onNotesChange,
  onReasonsChange,
  onBack,
  onNext,
  onSubmit,
  isLastTrade,
  uniqueLink,
  isCreator = false,
  submitError = '',
  submitting = false,
}) => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentIndex]);

  const tradePlayerIds = useMemo(() => {
    const ids = new Set();
    (trade.creatorItems || []).forEach((i) => {
      if (i.type === 'player' && i.playerId) ids.add(String(i.playerId));
    });
    (trade.recipientItems || []).forEach((i) => {
      if (i.type === 'player' && i.playerId) ids.add(String(i.playerId));
    });
    return ids;
  }, [trade]);

  const tradePlayerNames = useMemo(() => {
    const names = [];
    tradePlayerIds.forEach((pid) => {
      const n = playerData?.[pid]?.name;
      if (n) names.push(n.toLowerCase());
    });
    return names;
  }, [tradePlayerIds, playerData]);

  const tradeTweets = useMemo(() => {
    if (!tweets || tweets.length === 0) return [];
    return tweets
      .filter((t) => {
        const associated = (t.players || []).some((p) =>
          tradePlayerIds.has(String(p.sleeperPlayerId))
        );
        if (associated) return true;
        const text = (t.text || '').toLowerCase();
        return tradePlayerNames.some((n) => text.includes(n));
      })
      .sort((a, b) => new Date(b.tweetCreatedAt) - new Date(a.tweetCreatedAt));
  }, [tweets, tradePlayerIds, tradePlayerNames]);

  const [hoverRating, setHoverRating] = useState(null);

  const getVerdictForRating = (n) => {
    if (n === 1) return { word: 'No way.', tone: 'hate' };
    if (n === 2) return { word: 'Hard pass.', tone: 'hate' };
    if (n === 3) return { word: 'Doubt it.', tone: 'hate' };
    if (n === 4) return { word: 'Tough sell.', tone: 'neutral' };
    if (n === 5) return { word: 'Maybe.', tone: 'neutral' };
    if (n === 6) return { word: 'Lean yes.', tone: 'neutral' };
    if (n === 7) return { word: 'Tempting.', tone: 'love' };
    if (n === 8) return { word: 'Likely.', tone: 'love' };
    if (n === 9) return { word: 'Almost done.', tone: 'love' };
    if (n === 10) return { word: 'Hell yes.', tone: 'love' };
    return { word: ' ', tone: 'neutral' };
  };

  const reasonOptions = useMemo(() => {
    const opts = [];

    const ordinal = (n) => {
      const num = Number(n);
      if (!Number.isFinite(num)) return `${n}`;
      const s = ['th', 'st', 'nd', 'rd'];
      const v = num % 100;
      return num + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const pickLabel = (pickData) =>
      pickData ? `${pickData.season} ${ordinal(pickData.round)}` : 'pick';

    const recipientItems = trade.recipientItems || [];
    const creatorItems = trade.creatorItems || [];

    recipientItems.forEach((item) => {
      if (item.type === 'player' && item.playerId) {
        const name = playerData?.[item.playerId]?.name;
        if (name) {
          opts.push({
            id: `wont_move:${item.playerId}`,
            label: `Not moving ${name}`,
          });
        }
      } else if (item.type === 'pick' && item.pickData) {
        opts.push({
          id: `wont_include:${item.id || pickLabel(item.pickData)}`,
          label: `Not including ${pickLabel(item.pickData)}`,
        });
      }
    });

    creatorItems.forEach((item) => {
      if (item.type === 'player' && item.playerId) {
        const name = playerData?.[item.playerId]?.name;
        if (name) {
          opts.push({
            id: `dont_want:${item.playerId}`,
            label: `Don't want ${name}`,
          });
        }
      } else if (item.type === 'pick' && item.pickData) {
        opts.push({
          id: `not_enough:${item.id || pickLabel(item.pickData)}`,
          label: `${pickLabel(item.pickData)} isn't enough`,
        });
      }
    });

    if (recipientItems.length >= 3 && creatorItems.length === 1) {
      opts.push({
        id: 'lopsided_count',
        label: `Don't want a ${recipientItems.length} for 1`,
      });
    }

    opts.push({ id: 'maybe_later', label: 'Maybe, but not right now' });
    opts.push({ id: 'get_younger', label: 'Trying to get younger' });
    opts.push({ id: 'need_starters', label: 'Need starters back' });
    opts.push({ id: 'want_picks', label: 'Looking for picks' });

    return opts;
  }, [trade, playerData]);

  const reasonPrompt = useMemo(() => {
    if (!rating) return '';
    if (rating <= 3) return "What's the dealbreaker?";
    return "What's holding you back?";
  }, [rating]);

  const selectedReasonIds = useMemo(
    () => new Set((reasons || []).map((r) => r.id)),
    [reasons]
  );

  const toggleReason = (option) => {
    if (!onReasonsChange) return;
    const isSelected = selectedReasonIds.has(option.id);
    if (isSelected) {
      onReasonsChange((reasons || []).filter((r) => r.id !== option.id));
    } else {
      onReasonsChange([...(reasons || []), option]);
    }
  };

  // Show the hover preview if no rating yet, else the locked-in rating.
  const displayRating = rating || hoverRating || null;
  const displayWord = displayRating ? getVerdictForRating(displayRating).word : '';

  const getPlayerName = (playerId) => {
    const player = playerData[playerId];
    if (!player) return 'Unknown Player';
    return player.name || 'Unknown Player';
  };

  const getPlayerMeta = (playerId) => {
    const player = playerData[playerId];
    if (!player) return '';
    return `${player.position} · ${player.team || 'FA'}`;
  };

  const generateKTCLink = () => {
    const teamGivePlayerIds = (trade.recipientItems || [])
      .filter(item => item.type === 'player' && item.playerId)
      .map(item => playerData[item.playerId]?.ktcId)
      .filter(Boolean);

    const teamGivePickIds = (trade.recipientItems || [])
      .filter(item => item.type === 'pick' && item.pickData)
      .map(item => getKTCPickIdFromPick(item.pickData))
      .filter(Boolean);

    const teamGiveIds = [...teamGivePlayerIds, ...teamGivePickIds];

    const teamGetPlayerIds = (trade.creatorItems || [])
      .filter(item => item.type === 'player' && item.playerId)
      .map(item => playerData[item.playerId]?.ktcId)
      .filter(Boolean);

    const teamGetPickIds = (trade.creatorItems || [])
      .filter(item => item.type === 'pick' && item.pickData)
      .map(item => getKTCPickIdFromPick(item.pickData))
      .filter(Boolean);

    const teamGetIds = [...teamGetPlayerIds, ...teamGetPickIds];

    if (teamGiveIds.length === 0 || teamGetIds.length === 0) {
      return null;
    }

    const format = league?.isSuperFlex ? '2' : '1';
    const tepValue = league?.tePremium || 0;
    const tepMap = { 0: '0', 0.5: '1', 1: '2', 1.5: '3' };
    const tep = tepMap[tepValue] || '0';

    return `https://keeptradecut.com/trade-calculator?var=5&pickVal=0&teamOne=${teamGiveIds.join('|')}&teamTwo=${teamGetIds.join('|')}&format=${format}&isStartup=0&tep=${tep}`;
  };

  const renderItemAvatar = (item, size = 28) => {
    if (item.type === 'player') {
      const player = playerData[item.playerId];
      if (player?.sleeperId) return <PlayerHeadshot playerId={player.sleeperId} size={size} />;
      if (player?.imageUrl) {
        return (
          <img
            src={player.imageUrl}
            alt=""
            style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flex: 'none', background: 'var(--ft-surface-hover)' }}
          />
        );
      }
      return <span style={smallCircle(size)}>{(player?.position || '?').slice(0, 2).toUpperCase()}</span>;
    }
    return (
      <span style={smallCircle(size)}>
        {item.pickData?.round ? `R${item.pickData.round}` : 'PK'}
      </span>
    );
  };

  const renderItemList = (items, label) => (
    <div style={{ minWidth: 0 }}>
      <span className="ft-k">{label}</span>
      <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, idx) => {
          const isPlayer = item.type === 'player';
          return (
            <li key={item.id || idx} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {renderItemAvatar(item)}
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {isPlayer
                    ? getPlayerName(item.playerId)
                    : item.pickData
                      ? `${item.pickData.season} ${ordinalRound(item.pickData.round)}`
                      : 'Draft pick'}
                </span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ft-ink-3)', marginTop: 1 }}>
                  {isPlayer ? getPlayerMeta(item.playerId) : 'Draft pick'}
                </span>
              </span>
            </li>
          );
        })}
        {items.length === 0 ? (
          <li style={{ fontSize: 13.5, color: 'var(--ft-ink-3)' }}>Nothing</li>
        ) : null}
      </ul>
    </div>
  );

  const canProceed = rating !== null && rating !== undefined;
  const ktcLink = generateKTCLink();

  return (
    <div>
      <style>{responsiveCss}</style>

      <span className="ft-k brand">
        Trade Package{totalTrades > 1 ? ` · Option ${currentIndex + 1} of ${totalTrades}` : ''}
      </span>
      <h1 className="ft-hero" style={{ fontSize: 30 }}>The offer.</h1>
      <p className="ft-sub">Take it in. Then make your call.</p>

      {/* The trade */}
      <div className="ft-card white" style={{ marginTop: 24 }}>
        <div className="ftpv-sides" style={{ display: 'grid', gridTemplateColumns: '1fr 28px 1fr', gap: 18, alignItems: 'start' }}>
          {renderItemList(trade.creatorItems || [], 'You get')}
          <div className="ftpv-arrow" style={{ display: 'flex', justifyContent: 'center', paddingTop: 30, color: 'var(--ft-ink-3)', fontSize: 15 }}>
            →
          </div>
          {renderItemList(trade.recipientItems || [], 'You send')}
        </div>
        {ktcLink ? (
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--ft-hairline)' }}>
            <a
              href={ktcLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ft-ink-3)', textDecoration: 'none' }}
            >
              Check it on KeepTradeCut →
            </a>
          </div>
        ) : null}
      </div>

      {uniqueLink && (
        <RostersPanel
          uniqueLink={uniqueLink}
          trade={trade}
          playerData={playerData}
          isCreator={isCreator}
        />
      )}

      {tradeTweets.length > 0 && (
        <div className="ft-section">
          <span className="ft-k">The chatter</span>
          <div
            style={{
              display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, marginTop: 12,
            }}
          >
            {tradeTweets.map((t) => (
              <div
                key={t.id}
                style={{ flex: '0 0 320px', maxWidth: 320, maxHeight: 360, overflowY: 'auto', display: 'flex' }}
              >
                <TweetCard tweet={t} showMetrics={false} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* The verdict */}
      <div className="ft-section" style={{ borderTop: '1px solid var(--ft-hairline)', paddingTop: 28 }}>
        <span className="ft-k">The verdict</span>

        <div style={{ minHeight: 52, display: 'flex', alignItems: 'flex-end', marginTop: 10 }}>
          <span
            key={displayRating || 'empty'}
            className="ftpv-verdictword"
            style={{
              fontSize: 40, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1,
              color: displayRating ? 'var(--ft-ink)' : 'var(--ft-disabled)',
            }}
          >
            {displayRating ? displayWord : 'Pick one.'}
          </span>
        </div>

        <p style={{ margin: '10px 0 0', fontSize: 14, color: 'var(--ft-ink-2)' }}>
          {rating
            ? `You rated this ${rating}/10.`
            : hoverRating
              ? `That's a ${hoverRating}/10. Lock it in?`
              : 'Tap a number to lock your call.'}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
          <span className="ft-k ftpv-anchor">Never</span>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
            const isSelected = rating === n;
            const isHovered = hoverRating === n && !isSelected;
            return (
              <button
                key={n}
                className="ftpv-circle"
                onClick={() => onRatingChange(n)}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(null)}
                aria-label={`Rate ${n} out of 10`}
                aria-pressed={isSelected}
                style={{
                  width: 44, height: 44, borderRadius: '50%', flex: 'none',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em',
                  background: isSelected ? 'var(--ft-dark)' : '#fff',
                  color: isSelected ? '#fff' : 'var(--ft-ink)',
                  border: isSelected
                    ? '1.5px solid var(--ft-dark)'
                    : isHovered
                      ? '1.5px solid var(--ft-ink)'
                      : '1.5px solid var(--ft-disabled)',
                  transform: isSelected ? 'scale(1.06)' : 'scale(1)',
                  transition: 'all .2s var(--ft-ease)',
                }}
              >
                {n}
              </button>
            );
          })}
          <span className="ft-k ftpv-anchor">Definitely</span>
        </div>

        {rating && rating <= 5 && reasonOptions.length > 0 ? (
          <div style={{ marginTop: 24 }}>
            <span className="ft-k">{reasonPrompt}</span>
            <div className="ft-pillrow" style={{ marginTop: 10 }}>
              {reasonOptions.map((option) => {
                const selected = selectedReasonIds.has(option.id);
                return (
                  <button
                    key={option.id}
                    className={`ft-fpill${selected ? ' on' : ''}`}
                    aria-pressed={selected}
                    onClick={() => toggleReason(option)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div style={{ marginTop: 24, maxWidth: 560 }}>
          <span className="ft-k">Add a note (optional)</span>
          <textarea
            rows={3}
            placeholder={rating ? 'Tell them why…' : 'Pick a rating first.'}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            disabled={!rating}
            style={{
              display: 'block', width: '100%', marginTop: 10,
              background: 'var(--ft-surface)', border: 'none', outline: 'none',
              borderRadius: 'var(--ft-r-input)', padding: '13px 16px',
              fontSize: 15, color: 'var(--ft-ink)', fontFamily: 'inherit',
              resize: 'vertical', lineHeight: 1.45,
              opacity: rating ? 1 : 0.55,
            }}
          />
        </div>
      </div>

      {submitError ? (
        <p style={{ margin: '20px 0 0', fontSize: 13.5, color: 'var(--ft-red)' }}>{submitError}</p>
      ) : null}

      <div className="ftpv-nav" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 28 }}>
        <button
          className="ft-btn ghost"
          onClick={onBack}
          disabled={currentIndex === 0}
          style={{ opacity: currentIndex === 0 ? 0.35 : 1 }}
        >
          Back
        </button>
        {isLastTrade ? (
          <button className="ft-btn" onClick={onSubmit} disabled={!canProceed || submitting}>
            {submitting ? 'Sending…' : 'Send it'}
          </button>
        ) : (
          <button className="ft-btn" onClick={onNext} disabled={!canProceed}>
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default TradeRatingView;
